import { supabase, handleError, getCurrentUser } from './config';
import { toast } from 'react-toastify';

/**
 * Supabase Service - Provides standardized methods for database operations
 */
class SupabaseService {
  /**
   * Get a record by ID
   * @param {string} table - Table name
   * @param {string} id - Record ID
   * @returns {Promise<Object>} Record data or null
   */
  static async getRecord(table, id) {
    try {
      const { data, error } = await supabase
        .from(table)
        .select()
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      handleError(error, `Error fetching ${table} record`);
      return null;
    }
  }
  
  /**
   * Query records from a table based on filters
   * @param {string} table - Table name
   * @param {Object} options - Query options
   * @param {Object} options.filters - Filter conditions
   * @param {Array<string>} options.select - Fields to select
   * @param {Array<Object>} options.order - Sort options
   * @param {number} options.limit - Limit of records
   * @param {number} options.offset - Offset for pagination
   * @returns {Promise<Array<Object>>} Array of records
   */
  static async queryRecords(table, options = {}) {
    try {
      let query = supabase.from(table).select(options.select || '*');
      
      // Apply filters if provided
      if (options.filters) {
        Object.keys(options.filters).forEach(key => {
          const value = options.filters[key];
          if (Array.isArray(value)) {
            query = query.in(key, value);
          } else {
            query = query.eq(key, value);
          }
        });
      }
      
      // Apply ordering if provided
      if (options.order) {
        options.order.forEach(orderItem => {
          if (typeof orderItem === 'string') {
            query = query.order(orderItem);
          } else {
            query = query.order(orderItem.column, { ascending: orderItem.ascending !== false });
          }
        });
      }
      
      // Apply pagination if provided
      if (options.limit) {
        query = query.limit(options.limit);
      }
      
      if (options.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data;
    } catch (error) {
      handleError(error, `Error querying ${table} records`);
      return [];
    }
  }
  
  /**
   * Add a record to a table
   * @param {string} table - Table name
   * @param {Object} data - Record data
   * @returns {Promise<Object>} Added record
   */
  static async addRecord(table, data) {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be authenticated to add records');
      }
      
      // Prepare data with user ID and timestamps
      const recordData = {
        ...data,
        user_id: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      // Insert record
      const { data: insertedData, error } = await supabase
        .from(table)
        .insert([recordData])
        .select();
      
      if (error) throw error;
      
      return insertedData[0];
    } catch (error) {
      console.error(`Error adding ${table} record:`, error);
      toast.error(`Failed to save ${table}. Please try again.`);
      throw error;
    }
  }
  
  /**
   * Update a record
   * @param {string} table - Table name
   * @param {string} id - Record ID
   * @param {Object} data - Fields to update
   * @returns {Promise<Object>} Updated record
   */
  static async updateRecord(table, id, data) {
    try {
      // Ensure user is logged in
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to update records');
      }
      
      // First check if record exists and belongs to current user
      const { data: existingRecord, error: fetchError } = await supabase
        .from(table)
        .select('user_id')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      if (!existingRecord) {
        throw new Error(`Record ${id} not found in ${table}`);
      }
      
      // Check ownership
      if (existingRecord.user_id !== currentUser.id) {
        throw new Error('Permission denied: You can only update your own records');
      }
      
      // Update the record
      const updateData = {
        ...data,
        updated_at: new Date().toISOString()
      };
      
      const { data: updatedData, error } = await supabase
        .from(table)
        .update(updateData)
        .eq('id', id)
        .select();
      
      if (error) throw error;
      console.log(`Updated ${table} record with ID: ${id}`);
      
      return updatedData[0];
    } catch (error) {
      handleError(error, `Error updating ${table} record`);
      throw error;
    }
  }
  
  /**
   * Delete a record
   * @param {string} table - Table name
   * @param {string} id - Record ID
   * @returns {Promise<void>}
   */
  static async deleteRecord(table, id) {
    try {
      // Ensure user is logged in
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to delete records');
      }
      
      // First check if record exists and belongs to current user
      const { data: existingRecord, error: fetchError } = await supabase
        .from(table)
        .select('user_id')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      if (!existingRecord) {
        throw new Error(`Record ${id} not found in ${table}`);
      }
      
      // Check ownership
      if (existingRecord.user_id !== currentUser.id) {
        throw new Error('Permission denied: You can only delete your own records');
      }
      
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      console.log(`Deleted ${table} record with ID: ${id}`);
    } catch (error) {
      handleError(error, `Error deleting ${table} record`);
      throw error;
    }
  }
  
  /**
   * Create user profile
   * @param {Object} profileData - Profile data
   * @returns {Promise<Object>} Created profile
   */
  static async createProfile(profileData) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .upsert({
          ...profileData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select();
      
      if (error) throw error;
      return data[0];
    } catch (error) {
      console.error('Error creating profile:', error);
      toast.error('Failed to create user profile');
      throw error;
    }
  }

  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   * @returns {Promise<Object>} Updated profile
   */
  static async updateUserProfile(profileData) {
    try {
      const currentUser = await supabase.auth.getUser();
      if (!currentUser.data.user) {
        throw new Error('You must be logged in to update your profile');
      }

      const { data, error } = await supabase
        .from('profiles')
        .update({
          ...profileData,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentUser.data.user.id)
        .select();
      
      if (error) throw error;
      return data[0];
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update user profile');
      throw error;
    }
  }
  
  /**
   * Add a recipe
   * @param {Object} recipeData - Recipe data
   * @returns {Promise<Object>} Added recipe
   */
  static async addRecipe(recipeData) {
    try {
      const currentUser = await supabase.auth.getUser();
      if (!currentUser.data.user) {
        throw new Error('You must be logged in to add a recipe');
      }

      const { data, error } = await supabase
        .from('recipes')
        .insert({
          ...recipeData,
          user_id: currentUser.data.user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select();
      
      if (error) throw error;
      return data[0];
    } catch (error) {
      console.error('Error adding recipe:', error);
      toast.error('Failed to add recipe');
      throw error;
    }
  }
  
  /**
   * Toggle recipe favorite status
   * @param {string} recipeId - Recipe ID
   * @param {boolean} isFavorite - Is favorite
   * @returns {Promise<Object>} Updated recipe
   */
  static async toggleFavorite(recipeId, isFavorite) {
    try {
      const currentUser = await supabase.auth.getUser();
      if (!currentUser.data.user) {
        throw new Error('You must be logged in to favorite a recipe');
      }

      const { data, error } = await supabase
        .from('recipes')
        .update({
          is_favorite: isFavorite,
          updated_at: new Date().toISOString()
        })
        .eq('id', recipeId)
        .eq('user_id', currentUser.data.user.id)
        .select();
      
      if (error) throw error;
      return data[0];
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorite status');
      throw error;
    }
  }
  
  /**
   * Get user's recipes
   * @param {boolean} favoritesOnly - Get only favorites
   * @returns {Promise<Array>} Array of recipes
   */
  static async getUserRecipes(favoritesOnly = false) {
    try {
      const currentUser = await supabase.auth.getUser();
      if (!currentUser.data.user) {
        throw new Error('You must be logged in to view your recipes');
      }

      let query = supabase
        .from('recipes')
        .select('*')
        .eq('user_id', currentUser.data.user.id)
        .order('created_at', { ascending: false });
        
      if (favoritesOnly) {
        query = query.eq('is_favorite', true);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting recipes:', error);
      toast.error('Failed to retrieve recipes');
      throw error;
    }
  }
  
  /**
   * Get user's meal plans
   * @returns {Promise<Array<Object>>} Array of meal plans
   */
  static async getUserMealPlans() {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to get meal plans');
      }
      
      return await this.queryRecords('meal_plans', {
        filters: { user_id: currentUser.id },
        order: [{ column: 'created_at', ascending: false }]
      });
    } catch (error) {
      handleError(error, 'Error fetching user meal plans');
      return [];
    }
  }
  
  /**
   * Get user's meal calendar entries for a specific date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise<Array<Object>>} Array of calendar entries
   */
  static async getMealCalendarEntries(startDate, endDate) {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to get calendar entries');
      }
      
      const { data, error } = await supabase
        .from('meal_calendar')
        .select('*')
        .eq('user_id', currentUser.id)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true });
      
      if (error) throw error;
      return data;
    } catch (error) {
      handleError(error, 'Error fetching meal calendar entries');
      return [];
    }
  }
  
  /**
   * Get user profile
   * @returns {Promise<Object>} User profile data
   */
  static async getUserProfile() {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to get profile');
      }
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', currentUser.id)
        .single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      handleError(error, 'Error fetching user profile');
      return null;
    }
  }
}

export default SupabaseService;
